import { Epic, combineEpics } from 'redux-observable';
import { filter, from, ignoreElements, map, mergeMap, of, switchMap, withLatestFrom } from 'rxjs';

import { catchAndLogError } from 'utils/common';
import { notNull } from 'utils/type_guards';
import { LOCALSTORAGE_COLLECTION_KEY } from 'utils/globals';

import session from 'domains/session';

import module from './index';
import * as photosAPI from './photos.api';

import type { Action } from '@reduxjs/toolkit';
import type { RootState } from 'store/wos_store';

const { actions, selectors } = module;

export const getAllPhotosEpic: Epic<Action, Action, RootState> = (action$) =>
  action$.pipe(
    filter(actions.getAll.pending.match),
    switchMap(() =>
      from(photosAPI.all()).pipe(
        map((photos) => actions.getAll.fulfilled(photos)),
        catchAndLogError(actions.getAll.rejected)
      )
    )
  );

/**
 * Take filestack upload data & save to API with a collection name
 */
export const uploadPhotosEpic: Epic<Action, Action, RootState> = (action$, state$) =>
  action$.pipe(
    filter(actions.uploadPhotos.pending.match),
    withLatestFrom(state$),
    mergeMap(([{ payload }, state]) => {
      const isLoggedIn = session.selectors.isLoggedIn(state);
      const newCollection = selectors.newUploadsCollection(state);

      const DEFAULT_COLLECTION_TOKEN = '__default__';

      const collection = isLoggedIn
        ? newCollection || payload.collection || DEFAULT_COLLECTION_TOKEN
        : DEFAULT_COLLECTION_TOKEN;

      const imageUrls: string[] = [];
      const imageNames: string[] = [];

      payload.uploads.forEach((value) => {
        imageUrls.push(value.url);
        imageNames.push(value.filename);
      });

      return from(photosAPI.bulkCreate(imageUrls, imageNames, collection)).pipe(
        map((photos) => actions.uploadPhotos.fulfilled(photos)),
        catchAndLogError(actions.uploadPhotos.rejected())
      );
    })
  );

/**
 * After uploading a photo, the thumbnail urls will not be in the response due to
 * background processing. This epic will refresh each photo uploaded to get the
 * thumbnail urls
 */
export const getNewUploadThumbnailUrlsEpic: Epic<Action, Action, RootState> = (action$) =>
  action$.pipe(
    filter(actions.uploadPhotos.fulfilled.match),
    map(({ payload }) => payload.map((p) => p.id)),
    mergeMap((ids) => from(ids)),
    mergeMap((id) =>
      from(photosAPI.find(id)).pipe(
        map((p) => actions.updateOne({ id, changes: p })),
        catchAndLogError()
      )
    )
  );

/**
 * Sort endpoint on the API requires the array of IDS in sorted order,
 * so we do that here based on the action payload of the ID of the moved photo
 * and the index in the array where it should go
 */
export const sortPhotosEpic: Epic<Action, Action, RootState> = (action$, state$) =>
  action$.pipe(
    filter(actions.sortPhotos.match),
    withLatestFrom(state$),
    mergeMap(([{ payload }, state]) => {
      const photoIds = selectors.selectIds(state) as number[]; // cast is ok, we know these ids are numbers
      const moved = selectors.selectById(state, payload.insertPhotoId);

      if (!moved) throw new Error(`No Photo with ID ${payload.insertPhotoId}`);

      const spliceFrom = moved.sort_index;

      let spliceAt;
      if (payload.beforeIndex === -1) {
        // Moving to end of list
        spliceAt = photoIds.length - 1;
      } else if (spliceFrom > payload.beforeIndex) {
        // Moving to left, don't account for moved photo's space
        spliceAt = payload.beforeIndex;
      } else {
        // Moving to right, need to account for sorted photo's space in the array
        spliceAt = payload.beforeIndex - 1;
      }

      const spliced = [...photoIds];
      spliced.splice(spliceFrom, 1);
      spliced.splice(spliceAt, 0, payload.insertPhotoId);

      return from(photosAPI.sort(spliced)).pipe(
        map(() => spliced.map((id, index) => ({ id, changes: { id, sort_index: index } }))),
        map((updatePayload) => actions.updateMany(updatePayload)),
        catchAndLogError()
      );
    })
  );

/**
 * Take all uncollected photos and add them to a new collection
 */
export const moveUncollectedToCollectionEpic: Epic<Action, Action, RootState> = (action$, state$) =>
  action$.pipe(
    filter(actions.moveUncollectedToCollection.match),
    withLatestFrom(state$),
    mergeMap(([{ payload }, state]) =>
      selectors
        .allUncollected(state)
        .map(({ id }) => actions.update.pending({ id, collection: payload }))
    )
  );

/**
 * When saving uncollected as a new collection, set active collection to the new one
 */
export const setActiveCollectionOnMoveUncollectedEpic: Epic<Action, Action, RootState> = (
  action$
) =>
  action$.pipe(
    filter(actions.moveUncollectedToCollection.match),
    map(({ payload }) => actions.setActiveCollection(payload))
  );

/**
 * Creates a new collection
 */
export const createCollectionEpic: Epic<Action, Action, RootState> = (action$) =>
  action$.pipe(
    filter(actions.createNewCollection.match),
    // Handling for this is covered by slice reducers, no need for additional actions
    filter(({ payload }) => payload.action !== 'create_from_upload'),
    mergeMap(({ payload }) => {
      switch (payload.action) {
        case 'create_from_existing':
          return payload.donorIds.map((id) =>
            actions.update.pending({ id, collection: payload.name })
          );
        case 'create_from_orphans':
          return of(actions.moveUncollectedToCollection(payload.name));
        default:
          return of(null);
      }
    }),
    filter(notNull)
  );

/**
 * Change the name of a collection
 */
export const renameCollectionPhotosEpic: Epic<Action, Action, RootState> = (action$, state$) =>
  action$.pipe(
    filter(actions.renameCollectionPhotos.pending.match),
    withLatestFrom(state$),
    mergeMap(([{ payload }, state]) =>
      from(
        Promise.all(
          selectors
            .allForCollection(state, payload.current)
            .map((p) => photosAPI.update(p.id, { id: p.id, collection: payload.change }))
        )
      ).pipe(map(actions.renameCollectionPhotos.fulfilled))
    )
  );

/**
 * Map update photo action to API request
 */
export const updatePhotoEpic: Epic<Action, Action, RootState> = (action$) =>
  action$.pipe(
    filter(actions.update.pending.match),
    mergeMap(({ payload }) =>
      from(photosAPI.update(payload.id, payload)).pipe(
        map((p) => actions.update.fulfilled(p)),
        catchAndLogError(actions.update.rejected)
      )
    )
  );

/**
 * Delete a photo on the API
 */
export const removePhotoEpic: Epic<Action, Action, RootState> = (action$, state$) =>
  action$.pipe(
    filter(actions.removePhoto.pending.match),
    withLatestFrom(state$),
    map(([{ payload }, state]) => selectors.selectById(state, payload)),
    filter(notNull),
    mergeMap((photo) =>
      from(photosAPI.destroy(photo.id)).pipe(
        map(() => actions.removePhoto.fulfilled(photo)),
        catchAndLogError(actions.removePhoto.rejected)
      )
    )
  );

/**
 * Map removeAllForCollection action to removeManyPhotos
 */
export const removeAllPhotosForCollectionEpic: Epic<Action, Action, RootState> = (
  action$,
  state$
) =>
  action$.pipe(
    filter(actions.removeAllForCollection.match),
    withLatestFrom(state$),
    map(([{ payload }, state]) => {
      const ids = selectors.allForCollection(state, payload).map((p) => p.id);
      return actions.removeManyPhotos.pending(ids);
    })
  );

/**
 * Delete multiple photos on the API
 */
export const removeManyPhotosEpic: Epic<Action, Action, RootState> = (action$) =>
  action$.pipe(
    filter(actions.removeManyPhotos.pending.match),
    mergeMap(({ payload }) =>
      from(photosAPI.destroyMany(payload)).pipe(
        map(() => actions.removeManyPhotos.fulfilled(payload)),
        catchAndLogError(actions.removeManyPhotos.rejected)
      )
    )
  );

/**
 * If a removed photo was the last of the active collection, or if an entire collection was removed,
 * set the active collection to the first remaining collection found,
 * otherwise the uncollected collection
 */
export const postDeletePhotoUpdateActiveCollectionEpic: Epic<Action, Action, RootState> = (
  action$,
  state$
) =>
  action$.pipe(
    filter(
      (action) =>
        actions.removePhoto.fulfilled.match(action) ||
        actions.removeManyPhotos.fulfilled.match(action)
    ),
    withLatestFrom(state$),
    map(([_, state]) => {
      const activeCollection = selectors.activeCollection(state);
      const remaining = selectors.allForCollection(state, activeCollection);

      if (remaining.length === 0) {
        const remaining = selectors.allCollectionNames(state);
        return actions.setActiveCollection(remaining[0] || null);
      }
    }),
    filter(notNull)
  );

/**
 * Populate active collection with the local storage saved value
 */
export const getSavedActiveCollectionEpic: Epic<Action, Action, RootState> = (action$) =>
  action$.pipe(
    filter(session.actions.getSession.fulfilled.match),
    filter(({ payload }) => !!payload.customer_num),
    map(() => localStorage.getItem(LOCALSTORAGE_COLLECTION_KEY)),
    filter(notNull),
    map(actions.setActiveCollection)
  );

/**
 * Set/unset the selected active collection in localstorage
 */
export const setLocalStorageActiveCollectionEpic: Epic<Action, Action, RootState> = (action$) =>
  action$.pipe(
    filter(actions.setActiveCollection.match),
    map(({ payload }) => {
      if (payload) {
        localStorage.setItem(LOCALSTORAGE_COLLECTION_KEY, payload);
      } else {
        localStorage.removeItem(LOCALSTORAGE_COLLECTION_KEY);
      }
    }),
    ignoreElements()
  );

const combined = combineEpics(
  getAllPhotosEpic,
  uploadPhotosEpic,
  getNewUploadThumbnailUrlsEpic,
  sortPhotosEpic,
  moveUncollectedToCollectionEpic,
  setActiveCollectionOnMoveUncollectedEpic,
  renameCollectionPhotosEpic,
  createCollectionEpic,
  updatePhotoEpic,
  removePhotoEpic,
  removeAllPhotosForCollectionEpic,
  removeManyPhotosEpic,
  postDeletePhotoUpdateActiveCollectionEpic,
  getSavedActiveCollectionEpic,
  setLocalStorageActiveCollectionEpic
);
export default combined;
